import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from 'shared/context/LanguageContext';
import type { PartnerWebStepEntry } from '../types';
import StageIcon from './stage-icon';

const HAAT_RED = '#C00035';

interface VerticalStepperProps {
  steps: PartnerWebStepEntry[];
}

const VerticalStepper: React.FC<VerticalStepperProps> = ({ steps }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const isHebrew = language !== 'en';
  const [openInfoKey, setOpenInfoKey] = useState<string | null>(null);

  return (
    <ol className="relative">
      <span
        aria-hidden="true"
        className="absolute top-0 bottom-0 w-px bg-gray-200"
        style={{ insetInlineStart: '23px' }}
      />
      {steps.map((step, idx) => {
        const isCompleted = step.status === 'completed';
        const isCurrent = step.status === 'current';
        const title = isHebrew ? step.titleHe : step.titleEn;
        const desc = isHebrew ? step.descriptionHe : step.descriptionEn;

        return (
          <motion.li
            key={step.key}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: idx * 0.05 }}
            className="relative ps-16 pb-6 last:pb-0"
          >
            <div className="absolute" style={{ insetInlineStart: 0, top: 0 }}>
              <div className="relative">
                {isCurrent && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-full animate-pulse-ring"
                    style={{ backgroundColor: `${HAAT_RED}4D` }}
                  />
                )}
                <div
                  className={`relative h-12 w-12 rounded-full flex items-center justify-center border-2 transition-colors ${
                    isCompleted
                      ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-200'
                      : isCurrent
                      ? 'bg-[#C00035] border-[#C00035] text-white shadow-lg shadow-[#C00035]/30'
                      : 'bg-white border-gray-200 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <StageIcon icon={step.icon} className="h-5 w-5" />
                  )}
                </div>
              </div>
            </div>

            <div
              className={`rounded-2xl border-2 px-4 py-3 transition-shadow ${
                isCurrent
                  ? 'bg-white border-[#C00035]/30 shadow-md shadow-[#C00035]/10'
                  : isCompleted
                  ? 'bg-white/60 border-gray-100'
                  : 'bg-gray-50/40 border-gray-100 opacity-70'
              }`}
            >
              <div className="flex items-start gap-2">
                <h4
                  className={`font-bold flex-1 ${
                    isCurrent ? 'text-[#C00035]' : 'text-gray-900'
                  }`}
                >
                  {title}
                </h4>
                {isCurrent && (
                  <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#C00035]/10 text-[#C00035] border border-[#C00035]/20">
                    {t('partnerPortal.stepper.current')}
                  </span>
                )}
                {isCompleted && (
                  <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                    {t('partnerPortal.stepper.completed')}
                  </span>
                )}
                <InfoButton
                  label={t('partnerPortal.stepper.info')}
                  description={desc}
                  isOpen={openInfoKey === step.key}
                  onToggle={() =>
                    setOpenInfoKey((prev) =>
                      prev === step.key ? null : step.key,
                    )
                  }
                  onClose={() => setOpenInfoKey(null)}
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">{desc}</p>
            </div>
          </motion.li>
        );
      })}
    </ol>
  );
};

interface InfoButtonProps {
  label: string;
  description: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const InfoButton: React.FC<InfoButtonProps> = ({
  label,
  description,
  isOpen,
  onToggle,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={containerRef} className="relative flex-shrink-0">
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-expanded={isOpen}
        onClick={onToggle}
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C00035]/50 ${
          isOpen
            ? 'bg-[#C00035] border-[#C00035] text-white'
            : 'bg-white border-gray-200 text-gray-400 hover:border-[#C00035]/40 hover:text-[#C00035]'
        }`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="tooltip"
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute z-20 mt-2 w-56 rounded-xl border border-gray-100 bg-white p-3 text-xs leading-relaxed text-gray-600 shadow-xl"
            style={{ insetInlineEnd: 0 }}
          >
            {description}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VerticalStepper;
